/* DalsiUdajeTab — „Další údaje" tab v Raynet 1:1 stylu (v4.21.4 Fáze 3).
 *
 * Obsahuje detail pole pro firmu (IČO/DIČ/datová schránka), osobní (narozeniny),
 * klasifikaci, platby (bank. spojení, splatnost, DPH), oslovení pro hromadné emaily.
 *
 * Design: SoftSection (jemná hlavička: ikona v --accent-soft + UPPERCASE --muted titulek +
 * tenká spodní hairline) napříč všemi sekcemi.
 *
 * Layout: 3-col grid 4-4-4 — vlevo Identifikace/Firma/Platby/Klasifikace,
 * uprostřed Komunikace + Osobní + Přílohy, vpravo Souhrn / KPI.
 *
 * Auto-save na onBlur (žádné „Uložit" tlačítko).
 */
import React, { useState, useEffect, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { RaynetSection, RaynetField, InputTight, SelectTight } from "@/components/crm/RaynetUI";
import { resolveSaveFieldKey, fieldChanged } from "@/lib/dalsiUdajeSave";
import AresAutocomplete from "@/components/crm/AresAutocomplete";
import AttachmentsList from "@/components/crm/AttachmentsList";
import GdprPanel from "@/components/crm/GdprPanel";
import { TL, localeUI } from "@/lib/prekladyUI";
import { odebirejZemi, zemeTenanta, menaZeme } from "@/lib/tenantMena";
import {
  IdCard, Building2, CreditCard, Tags, MessageSquare, Cake, Paperclip, MapPin, FileText,
} from "lucide-react";

const SALUTATION_OPTIONS = [
  { v: "", label: "—" },
  { v: "Pan", label: "Pan" },
  { v: "Paní", label: "Paní" },
  { v: "Slečna", label: "Slečna" },
  { v: "Vážený pane", label: "Vážený pane" },
  { v: "Vážená paní", label: "Vážená paní" },
];

/* Firemní údaje podle ZEMĚ TENANTA (rozhodnutí majitele 19. 10. 2026, CRM-SK-17):
 * slovenský klient má IČ DPH, zápis v ORSR, elektronickou schránku a SZČO;
 * ARES je český rejstřík, u SK se nenabízí. Klíče v DB (dic, data_box,
 * spis_znacka, legal_form „OSVČ“) zůstávají — mění se jen to, co poradce vidí. */
const POLE_ZEME = {
  CZ: { dic: "DIČ", dicPh: "CZ12345678", box: "Datová schránka", boxPh: "abc7def8", spis: "Spisová značka", spisPh: "C 12345/MSPH", www: "www.priklad.cz", ares: true },
  SK: { dic: "IČ DPH", dicPh: "SK1234567890", box: "Elektronická schránka", boxPh: "", spis: "Zápis v ORSR", spisPh: "Sro 12345/B", www: "www.priklad.sk", ares: false },
};
const LEGAL_FORM_OPTIONS_SK = [
  { v: "", label: "—" },
  { v: "OSVČ", label: "SZČO / fyzická osoba" },
  { v: "s.r.o.", label: "s.r.o." },
  { v: "a.s.", label: "a.s." },
  { v: "v.o.s.", label: "v.o.s." },
  { v: "k.s.", label: "k.s." },
  { v: "z.s.", label: "o.z. / združenie" },
  { v: "družstvo", label: "Družstvo" },
  { v: "jiné", label: "Iné" },
];

const LEGAL_FORM_OPTIONS = [
  { v: "", label: "—" },
  { v: "OSVČ", label: "OSVČ / Fyzická osoba" },
  { v: "s.r.o.", label: "s.r.o." },
  { v: "a.s.", label: "a.s." },
  { v: "v.o.s.", label: "v.o.s." },
  { v: "k.s.", label: "k.s." },
  { v: "z.s.", label: "z.s. / Spolek" },
  { v: "družstvo", label: "Družstvo" },
  { v: "jiné", label: "Jiné" },
];

const PAYMENT_TERMS_OPTIONS = [
  { v: "", label: "—" },
  { v: "hotově", label: "Hotově" },
  { v: "7 dní", label: "Splatnost 7 dní" },
  { v: "14 dní", label: "Splatnost 14 dní" },
  { v: "30 dní", label: "Splatnost 30 dní" },
  { v: "60 dní", label: "Splatnost 60 dní" },
];

export default function DalsiUdajeTab({ contact, onPatch }) {
  /* Bez useLanguage(): záložka se renderuje i mimo LanguageProvider (testy, náhledy).
   * Jazyk = globální jazyk UI (TL), měna = země tenanta (store). */
  const zeme = useSyncExternalStore(odebirejZemi, zemeTenanta, zemeTenanta);
  const P = POLE_ZEME[zeme] || POLE_ZEME.CZ;
  const L = (x) => TL(x);
  const symbol = menaZeme(zeme).symbol;
  const [form, setForm] = useState(() => initForm(contact));
  useEffect(() => { setForm(initForm(contact)); }, [contact?.id]); // eslint-disable-line

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const persist = async (patch) => {
    try {
      await onPatch(patch);
      toast.success(L("Uloženo"));
    } catch { /* parent shows error toast */ }
  };

  return (
    <div className="px-4 md:px-5 py-3" data-testid="tab-dalsi">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">

        {/* ───────────── LEFT col (4/12) — Identifikace + Firma + Platby ───────────── */}
        <div className="md:col-span-4 space-y-1.5">

          <RaynetSection title={L("Identifikace")} icon={IdCard} extra={P.ares && (
            <span className="text-popisek font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
              style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid var(--accent)" }}
              title="Vyhledat v ARES"
            >ARES</span>
          )}>
            {P.ares && (
            <RaynetField label="Vyhledat">
              <AresAutocomplete
                value={form.ico || ""}
                onChange={(v) => setField("ico", v)}
                testid="dalsi-ares-search"
                placeholder="IČO nebo název firmy…"
                onPick={(entity) => {
                  const addr = entity.address || {};
                  const patch = {
                    ico: entity.ico || "",
                    legal_form: entity.is_person ? "OSVČ" : (form.legal_form || ""),
                  };
                  setForm((f) => ({ ...f, ...patch }));
                  persist(patch);
                  const addrPatch = {};
                  if (addr.street) addrPatch.street = addr.street;
                  if (addr.city) addrPatch.city = addr.city;
                  if (addr.psc) addrPatch.psc = addr.psc;
                  if (addr.region) addrPatch.region = addr.region;
                  if (Object.keys(addrPatch).length > 0 && contact) {
                    // Kraj z ARES jde rovnou do kontaktu. Pole „Region (kraj)“
                    // v panelu Zařazení čte `contact.region` přímo, ne přes
                    // formulář — takže se srovná samo, jakmile rodič překreslí.
                    onPatch(addrPatch).catch(() => {});
                  }
                  toast.success(`Načteno z ARES — ${entity.name}`);
                }}
              />
            </RaynetField>
            )}
            <RaynetField label="IČO">
              <InputTight value={form.ico} onChange={(v) => setField("ico", v)} testid="dalsi-ico" placeholder="12345678" />
            </RaynetField>
            <RaynetField label={P.dic}>
              <InputTight value={form.dic} onChange={(v) => setField("dic", v)} testid="dalsi-dic" placeholder={P.dicPh} />
            </RaynetField>
            <RaynetField label={P.box}>
              <InputTight value={form.data_box} onChange={(v) => setField("data_box", v)} testid="dalsi-data-box" placeholder={P.boxPh} />
            </RaynetField>
            <RaynetField label={P.spis}>
              <InputTight value={form.spis_znacka || ""} onChange={(v) => setField("spis_znacka", v)} testid="dalsi-spis-znacka" placeholder={P.spisPh} />
            </RaynetField>
            <SaveBlock fields={["ico", "dic", "data_box", "spis_znacka"]} form={form} contact={contact} onSave={persist} />
          </RaynetSection>

          <RaynetSection title={L("Firma")} icon={Building2}>
            <RaynetField label={L("Právní forma")}>
              <SelectTight
                value={form.legal_form}
                onChange={(v) => { setField("legal_form", v); persist({ legal_form: v }); }}
                testid="dalsi-legal-form"
                options={zeme === "SK" ? LEGAL_FORM_OPTIONS_SK : LEGAL_FORM_OPTIONS}
              />
            </RaynetField>
            <RaynetField label={`${L("Obrat")} (${symbol})`}>
              <InputTight type="number" value={form.revenue ?? ""} onChange={(v) => setField("revenue", v === "" ? null : Number(v))} testid="dalsi-revenue" placeholder="0" />
            </RaynetField>
            <RaynetField label={L("Zaměstnanců")}>
              <InputTight type="number" value={form.employees ?? ""} onChange={(v) => setField("employees", v === "" ? null : Number(v))} testid="dalsi-employees" placeholder="0" />
            </RaynetField>
            <RaynetField label={L("Plátce DPH")}>
              <div className="flex gap-1" data-testid="dalsi-vat-payer">
                {[{ v: null, label: "—" }, { v: true, label: L("Ano") }, { v: false, label: L("Ne") }].map((o) => {
                  const active = form.vat_payer === o.v;
                  return (
                    <button
                      key={String(o.v)}
                      onClick={() => { setField("vat_payer", o.v); persist({ vat_payer: o.v }); }}
                      className="flex-1 py-1 rounded text-drobny font-bold transition-colors"
                      style={{
                        background: active ? "var(--accent)" : "var(--surface)",
                        color: active ? "var(--on-accent)" : "var(--muted)",
                        border: `1px solid ${active ? "var(--accent)" : "var(--line)"}`,
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </RaynetField>
            <SaveBlock fields={["revenue", "employees"]} form={form} contact={contact} onSave={persist} />
          </RaynetSection>

          <RaynetSection title={L("Platby")} icon={CreditCard}>
            <RaynetField label={L("Bank. účet")}>
              <InputTight value={form.bank_account} onChange={(v) => setField("bank_account", v)} testid="dalsi-bank-account" placeholder="123456789/0100" />
            </RaynetField>
            <RaynetField label={L("Splatnost")}>
              <SelectTight
                value={form.payment_terms}
                onChange={(v) => { setField("payment_terms", v); persist({ payment_terms: v }); }}
                testid="dalsi-payment-terms"
                options={PAYMENT_TERMS_OPTIONS.map((o) => ({ ...o, label: L(o.label) }))}
              />
            </RaynetField>
            <SaveBlock fields={["bank_account"]} form={form} contact={contact} onSave={persist} />
          </RaynetSection>

          <RaynetSection title={L("Klasifikace")} icon={Tags}>
            <RaynetField label={L("Klasifikace 1")}>
              <InputTight value={form.classification_1} onChange={(v) => setField("classification_1", v)} testid="dalsi-classification-1" placeholder={L("zákazník / dodavatel")} />
            </RaynetField>
            <RaynetField label={L("Klasifikace 2")}>
              <InputTight value={form.classification_2} onChange={(v) => setField("classification_2", v)} testid="dalsi-classification-2" />
            </RaynetField>
            <RaynetField label={L("Klasifikace 3")}>
              <InputTight value={form.classification_3} onChange={(v) => setField("classification_3", v)} testid="dalsi-classification-3" />
            </RaynetField>
            <SaveBlock fields={["classification_1", "classification_2", "classification_3"]} form={form} contact={contact} onSave={persist} />
          </RaynetSection>
        </div>

        {/* ───────────── MIDDLE col (4/12) — Komunikace + Osobní + Přílohy ───────────── */}
        <div className="md:col-span-4 space-y-1.5">

          <RaynetSection title={L("Komunikace")} icon={MessageSquare}>
            <RaynetField label={L("Oslovení")}>
              <SelectTight
                value={form.salutation}
                onChange={(v) => { setField("salutation", v); persist({ salutation: v }); }}
                testid="dalsi-salutation"
                options={SALUTATION_OPTIONS}
              />
            </RaynetField>
            <div className="text-drobny italic mt-1 mb-1.5" style={{ color: "var(--faint)" }}>
              {L("Použijeme automaticky v šablonách")} („Vážená paní {contact?.surname || 'Nováková'}…“)
            </div>
            <RaynetField label="Web (WWW)">
              <InputTight value={form.www} onChange={(v) => setField("www", v)} testid="dalsi-www" placeholder={P.www} />
            </RaynetField>
            <SaveBlock fields={["www"]} form={form} contact={contact} onSave={persist} />
          </RaynetSection>

          <RaynetSection title={L("Osobní")} icon={Cake}>
            <RaynetField label={L("Narozeniny")}>
              <InputTight type="date" value={form.anniversary} onChange={(v) => setField("anniversary", v || null)} testid="dalsi-anniversary" />
            </RaynetField>
            <div className="text-drobny italic" style={{ color: "var(--faint)" }}>
              {L("AI vám připomene 7 dní předem (pošle do diáře úkol)")}
            </div>
            <SaveBlock fields={["anniversary"]} form={form} contact={contact} onSave={persist} />
          </RaynetSection>

          <RaynetSection title={L("Přílohy")} icon={Paperclip} extra={
            <span className="text-drobny font-semibold" style={{ color: "var(--faint)" }}>PDF · Word · Excel · Foto</span>
          }>
            <AttachmentsList contactId={contact?.id} />
          </RaynetSection>
        </div>

        {/* ───────────── RIGHT col (4/12) — Souhrn / Zařazení ───────────── */}
        <div className="md:col-span-4 space-y-1.5">

          <RaynetSection title={L("Zařazení")} icon={MapPin}>
            <RaynetField label={L("Skupina")}>
              <InputTight value={form.group || ""} onChange={(v) => setField("group", v)} testid="dalsi-group" placeholder="—" />
            </RaynetField>
            {/* „Region“ tady NENÍ obchodní region — je to KRAJ ZE SÍDLA KLIENTA.
                Nese ho `contact.region` (backend/models.py: `region  # kraj`), tedy
                TÁŽ databázová položka, kterou záložka Přehled → Sídlo klienta
                zobrazuje a edituje pod názvem „Kraj“. Předloha (Raynet) ho
                v Zařazení taky jen odvozuje z adresy.

                PROČ JE JEN KE ČTENÍ: krátce byl editovatelný a to byla horší chyba
                než mrtvý input, do kterého nešlo psát. Panel se jmenuje „Zařazení“
                a sousedí se „Skupinou“ — poradce sem logicky napíše obchodní region
                („Sever“, „Morava — tým 3“), blur uloží PUT s region=Sever, systém
                potvrdí zeleným „Uloženo“ a v adrese klienta je od té chvíle
                Kraj = Sever. Tichý přepis adresního údaje, o kterém se poradce
                nemá jak dozvědět. (Stejně to platí i opačným směrem.)
                Obchodní region má vlastní pole: Přehled → Teritorium (`territory`). */}
            <RaynetField label={L("Region (kraj)")}>
              <InputTight
                value={contact?.region || ""}
                readOnly
                testid="dalsi-region"
                placeholder="—"
                title={L("Kraj ze sídla klienta — mění se na záložce Přehled → Sídlo klienta")}
              />
            </RaynetField>
            <div className="text-drobny italic mb-1.5" style={{ color: "var(--faint)" }}>
              {L("Kraj z adresy (Přehled → Sídlo klienta). Obchodní region patří do pole Teritorium tamtéž.")}
            </div>
            {/* Vlastník = poradce, kterému kontakt patří (owner_id v DB).
                POZOR — dřív tu bylo natvrdo `contact?.owner_name || "Poradce"`.
                Jenže `owner_name` backend neposílá NIKDE, takže se u 100 % klientů
                a u každého poradce zobrazovalo slovo „Poradce“ — konstanta ze
                zdrojáku, která vypadala jako uložený údaj (a v manažerském pohledu
                na cizí klienty mátla nejvíc). Vymyšlený údaj tu být nesmí:
                jméno ukážeme jen když ho backend pošle, jinak „—“ (neznáme),
                a když kontakt vlastníka nemá, tak „nepřiřazeno“.
                Měnit vlastníka z téhle záložky nejde → readOnly, ne mrtvý input. */}
            <RaynetField label={L("Vlastník")}>
              <InputTight
                value={L(ownerLabel(contact))}
                readOnly
                testid="dalsi-owner"
                title={L("Vlastníka kontaktu nelze z této záložky měnit")}
              />
            </RaynetField>
            {/* Jen „Skupina“ — Region se odsud NEUKLÁDÁ (je to kraj z adresy). */}
            <SaveBlock fields={["group"]} form={form} contact={contact} onSave={persist} />
          </RaynetSection>

          <RaynetSection title={L("Souhrn údajů")} icon={FileText}>
            <div className="space-y-1 text-drobny" style={{ color: "var(--ink)" }}>
              <SummaryRow label={L("Oslovení")} value={form.salutation || "—"} />
              <SummaryRow label="IČO" value={form.ico || "—"} mono />
              <SummaryRow label={P.dic} value={form.dic || "—"} mono />
              <SummaryRow label={L("Právní forma")} value={(zeme === "SK" && (LEGAL_FORM_OPTIONS_SK.find((o) => o.v && o.v === form.legal_form) || {}).label) || form.legal_form || "—"} />
              {/* Obrat v měně TENANTA (dřív natvrdo „Kč“ i u slovenského poradce). */}
              <SummaryRow label={L("Obrat")} value={Number.isFinite(Number(form.revenue)) && Number(form.revenue) ? `${Number(form.revenue).toLocaleString(localeUI())}\u00a0${symbol}` : "—"} mono />
              <SummaryRow label={L("Zaměstnanců")} value={form.employees ?? "—"} mono />
              <SummaryRow label={L("Plátce DPH")} value={form.vat_payer === true ? L("Ano") : form.vat_payer === false ? L("Ne") : "—"} />
              <SummaryRow label={L("Bank. účet")} value={form.bank_account || "—"} mono />
              <SummaryRow label={L("Splatnost")} value={form.payment_terms || "—"} />
              <SummaryRow label={P.box} value={form.data_box || "—"} mono />
              <SummaryRow label={L("Narozeniny")} value={form.anniversary || "—"} mono />
            </div>
          </RaynetSection>

          <GdprPanel contact={contact} onChanged={() => { onPatch && onPatch({}).catch(() => {}); }} />
        </div>
      </div>
    </div>
  );
}

/* SaveBlock — invisible component that handles blur-save lifecycle for fields */
function SaveBlock({ fields, form, contact, onSave }) {
  useEffect(() => {
    const handleBlur = (e) => {
      const t = e.target;
      if (!t || t.tagName !== "INPUT") return;
      const fieldKey = resolveSaveFieldKey(t.getAttribute("data-testid"), fields);
      if (!fieldKey) return;
      if (!fieldChanged(form, contact, fieldKey)) return;
      onSave({ [fieldKey]: form[fieldKey] });
    };
    document.addEventListener("blur", handleBlur, true);
    return () => document.removeEventListener("blur", handleBlur, true);
  }, [fields, form, contact, onSave]);
  return null;
}

function SummaryRow({ label, value, mono = false }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-popisek uppercase tracking-wide font-medium flex-shrink-0" style={{ color: "var(--muted)" }}>
        {label}
      </span>
      <span className={`text-drobny font-semibold truncate text-right ${mono ? "mono tabular-nums" : ""}`} style={{ color: value === "—" ? "var(--faint)" : "var(--ink)" }}>
        {value}
      </span>
    </div>
  );
}

/* Popisek pole „Vlastník“. Zásada: nikdy nedosazuj vymyšlené jméno.
 *  - backend pošle jméno (owner_name) → ukaž ho,
 *  - kontakt vlastníka MÁ, ale jméno neznáme (backend zatím posílá jen owner_id)
 *    → „—“, tedy poctivé „neznáme“; ne zástupné slovo, které vypadá jako údaj,
 *  - kontakt vlastníka nemá → „nepřiřazeno“, což je skutečná informace.
 * Rozlišení owner_id vs. nic je důležité: „nepřiřazeno“ u kontaktu, který
 * vlastníka má, by byla jen jiná lež. */
export function ownerLabel(contact) {
  const name = typeof contact?.owner_name === "string" ? contact.owner_name.trim() : "";
  if (name) return name;
  return contact?.owner_id ? "—" : "nepřiřazeno";
}

function initForm(c) {
  if (!c) return {};
  return {
    salutation: c.salutation || "",
    www: c.www || "",
    ico: c.ico || "",
    dic: c.dic || "",
    data_box: c.data_box || "",
    spis_znacka: c.spis_znacka || "",
    legal_form: c.legal_form || "",
    revenue: c.revenue ?? null,
    employees: c.employees ?? null,
    vat_payer: c.vat_payer ?? null,
    bank_account: c.bank_account || "",
    payment_terms: c.payment_terms || "",
    anniversary: c.anniversary || "",
    classification_1: c.classification_1 || "",
    classification_2: c.classification_2 || "",
    classification_3: c.classification_3 || "",
    group: c.group || "",
    // `region` (kraj z adresy) tu ZÁMĚRNĚ NENÍ: pole je jen ke čtení a čte
    // contact.region přímo. Kdyby prošlo formulářem, vznikl by z něj druhý
    // zápisový kanál nad adresním údajem — přesně to, co jsme opravovali.
  };
}
